// Sound notification service for new orders
// Uses Web Audio API to generate sounds programmatically

class SoundService {
  constructor() {
    this.audioContext = null;
    this.sounds = {
      default: this.createDefaultSound,
      alert: this.createAlertSound,
      bell: this.createBellSound,
      cash: this.createCashSound,
      chime: this.createChimeSound
    };
    this.volume = 1.0;
  }

  getAudioContext() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this.audioContext;
  }

  setVolume(vol) {
    this.volume = Math.max(0, Math.min(1, vol / 100));
  }

  // Default notification sound - pleasant two-tone
  createDefaultSound(ctx, gainNode) {
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    
    osc1.type = 'sine';
    osc2.type = 'sine';
    osc1.frequency.setValueAtTime(880, ctx.currentTime);
    osc2.frequency.setValueAtTime(1108.73, ctx.currentTime); // C#6
    
    osc1.connect(gainNode);
    osc2.connect(gainNode);
    
    gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
    
    osc1.start(ctx.currentTime);
    osc2.start(ctx.currentTime);
    osc1.stop(ctx.currentTime + 0.5);
    osc2.stop(ctx.currentTime + 0.5);
  }

  // Alert sound - urgent beeping
  createAlertSound(ctx, gainNode) {
    for (let i = 0; i < 3; i++) {
      const osc = ctx.createOscillator();
      osc.type = 'square';
      osc.frequency.setValueAtTime(800, ctx.currentTime + i * 0.2);
      osc.connect(gainNode);
      
      gainNode.gain.setValueAtTime(0.2, ctx.currentTime + i * 0.2);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.2 + 0.1);
      
      osc.start(ctx.currentTime + i * 0.2);
      osc.stop(ctx.currentTime + i * 0.2 + 0.15);
    }
  }

  // Bell sound - classic notification bell
  createBellSound(ctx, gainNode) {
    const frequencies = [523.25, 659.25, 783.99]; // C5, E5, G5 chord
    
    frequencies.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, ctx.currentTime);
      osc.connect(gainNode);
      
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.8);
    });
    
    gainNode.gain.setValueAtTime(0.25, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.8);
  }

  // Cash register sound - cha-ching!
  createCashSound(ctx, gainNode) {
    // First part - mechanical click
    const noise = ctx.createOscillator();
    noise.type = 'sawtooth';
    noise.frequency.setValueAtTime(150, ctx.currentTime);
    noise.frequency.exponentialRampToValueAtTime(50, ctx.currentTime + 0.05);
    noise.connect(gainNode);
    noise.start(ctx.currentTime);
    noise.stop(ctx.currentTime + 0.05);
    
    // Second part - bell ring
    const bell1 = ctx.createOscillator();
    const bell2 = ctx.createOscillator();
    bell1.type = 'sine';
    bell2.type = 'sine';
    bell1.frequency.setValueAtTime(1567.98, ctx.currentTime + 0.1); // G6
    bell2.frequency.setValueAtTime(2093, ctx.currentTime + 0.1); // C7
    
    bell1.connect(gainNode);
    bell2.connect(gainNode);
    
    gainNode.gain.setValueAtTime(0.3, ctx.currentTime + 0.1);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.6);
    
    bell1.start(ctx.currentTime + 0.1);
    bell2.start(ctx.currentTime + 0.1);
    bell1.stop(ctx.currentTime + 0.6);
    bell2.stop(ctx.currentTime + 0.6);
  }

  // Chime sound - gentle melodic notification
  createChimeSound(ctx, gainNode) {
    const notes = [
      { freq: 783.99, time: 0 },      // G5
      { freq: 987.77, time: 0.15 },   // B5
      { freq: 1174.66, time: 0.3 },   // D6
      { freq: 1567.98, time: 0.45 }   // G6
    ];
    
    notes.forEach(note => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(note.freq, ctx.currentTime + note.time);
      osc.connect(gainNode);
      
      const noteGain = ctx.createGain();
      osc.connect(noteGain);
      noteGain.connect(gainNode);
      noteGain.gain.setValueAtTime(0.2, ctx.currentTime + note.time);
      noteGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + note.time + 0.4);
      
      osc.start(ctx.currentTime + note.time);
      osc.stop(ctx.currentTime + note.time + 0.4);
    });
    
    gainNode.gain.setValueAtTime(0.25, ctx.currentTime);
  }

  // Play a sound by type
  play(soundType = 'default') {
    try {
      const ctx = this.getAudioContext();
      
      // Resume audio context if suspended (browser autoplay policy)
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      
      const gainNode = ctx.createGain();
      gainNode.connect(ctx.destination);
      gainNode.gain.value = this.volume;
      
      const soundFn = this.sounds[soundType] || this.sounds.default;
      soundFn.call(this, ctx, gainNode);
      
      return true;
    } catch (e) {
      console.error('Error playing sound:', e);
      return false;
    }
  }

  // Preview a sound (for settings page)
  preview(soundType) {
    return this.play(soundType);
  }

  // Get available sound types with labels
  getSoundTypes() {
    return [
      { value: 'default', label: 'Padrão', description: 'Som suave de duas notas' },
      { value: 'alert', label: 'Alerta', description: 'Bipes urgentes' },
      { value: 'bell', label: 'Sino', description: 'Som de sino clássico' },
      { value: 'cash', label: 'Caixa Registradora', description: 'Som de venda!' },
      { value: 'chime', label: 'Carrilhão', description: 'Melodia suave ascendente' }
    ];
  }
}

const soundService = new SoundService();
export default soundService;
